import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Combobox } from "@/components/ui/combobox";
import { Input } from "@/components/ui/input";
import { ContractTypeOptions } from "@/lib/api/otc";
import { SelectOptionItem } from "@/lib/models/ui";
import { AMENDMENT_TYPE_OPTIONS } from "@/section/otc/contract-form-v3/amendmentTypes";
import { OrderType, OtcContractMode, REOWN_CONTRACT_TYPES } from "./types";

const HEDGEABLE_OPTIONS = ContractTypeOptions.filter((o) => !REOWN_CONTRACT_TYPES.has(o.value));
const REOWN_OPTIONS = ContractTypeOptions.filter((o) => REOWN_CONTRACT_TYPES.has(o.value));

/** `AMENDMENT_TYPE_OPTIONS` as combobox options; the option `value` is what gets persisted. */
const AMENDMENT_TYPE_SELECT_OPTIONS: SelectOptionItem<string>[] = AMENDMENT_TYPE_OPTIONS.map(
  (option) => ({ value: option.value, label: option.label }),
);

interface OrderTypeFieldProps {
  orderType: OrderType;
  otcContractMode: OtcContractMode | null;
  otcContractType: string;
  amendmentType: string;
  contractNumber: string;
  errorOrderType?: string;
  errorOtcContractType?: string;
  errorAmendmentType?: string;
  suggestionOrderType?: OrderType;
  showOrderTypeSuggestion: boolean;
  onChangeOrderType: (v: OrderType) => void;
  onChangeOtcContractMode: (v: OtcContractMode) => void;
  onChangeOtcContractType: (v: string) => void;
  onChangeAmendmentType: (v: string) => void;
  onChangeContractNumber: (v: string) => void;
  onApplyOrderTypeSuggestion: () => void;
  isEditable: boolean;
}

const ORDER_TYPE_LABELS: Record<OrderType, string> = {
  none: "None",
  hta: "HTA",
  cash: "Cash",
  otc: "OTC",
  pricing: "Pricing",
  position_adjust: "Position Adjust",
};

export function OrderTypeField({
  isEditable,
  orderType,
  otcContractMode,
  otcContractType,
  amendmentType,
  contractNumber,
  errorOrderType,
  errorOtcContractType,
  errorAmendmentType,
  suggestionOrderType,
  showOrderTypeSuggestion,
  onChangeOrderType,
  onChangeOtcContractMode,
  onChangeOtcContractType,
  onChangeAmendmentType,
  onChangeContractNumber,
  onApplyOrderTypeSuggestion,
}: OrderTypeFieldProps) {
  const availableOtcContractTypes =
    otcContractMode === "reown" ? REOWN_OPTIONS : otcContractMode === "hedgeable" ? HEDGEABLE_OPTIONS : [];

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-3">
        <Label>
          Order Type <span className="text-destructive">*</span>
        </Label>
        {showOrderTypeSuggestion && suggestionOrderType && (
          <Button
            disabled={!isEditable}
            type="button"
            variant="outline"
            size="sm"
            onClick={onApplyOrderTypeSuggestion}
            className="text-xs h-7"
          >
            Apply suggestion: {ORDER_TYPE_LABELS[suggestionOrderType]}
          </Button>
        )}
      </div>

      <RadioGroup
        disabled={!isEditable}
        value={orderType}
        onValueChange={(v) => onChangeOrderType(v as OrderType)}
        className="flex flex-wrap gap-x-4 gap-y-1"
      >
        {(Object.keys(ORDER_TYPE_LABELS) as OrderType[]).map((type) => (
          <div key={type} className="flex items-center space-x-2">
            <RadioGroupItem value={type} id={`ttv2-type-${type}`} />
            <Label htmlFor={`ttv2-type-${type}`} className="font-normal cursor-pointer">
              {ORDER_TYPE_LABELS[type]}
            </Label>
          </div>
        ))}
      </RadioGroup>

      {errorOrderType && <p className="text-destructive text-xs">{errorOrderType}</p>}

      {orderType === "otc" && (
        <div className="space-y-3 pl-4 border-l-2 border-muted">
          <div className="flex gap-2">
            <Button
              disabled={!isEditable}
              type="button"
              variant={otcContractMode === "hedgeable" ? "default" : "outline"}
              size="sm"
              onClick={() => onChangeOtcContractMode("hedgeable")}
            >
              Hedgeable
            </Button>
            <Button
              disabled={!isEditable}
              type="button"
              variant={otcContractMode === "reown" ? "default" : "outline"}
              size="sm"
              onClick={() => onChangeOtcContractMode("reown")}
            >
              Reown
            </Button>
            <Button
              disabled={!isEditable}
              type="button"
              variant={otcContractMode === "amendment" ? "default" : "outline"}
              size="sm"
              onClick={() => onChangeOtcContractMode("amendment")}
            >
              Amendment
            </Button>
          </div>

          {otcContractMode === "amendment" && (
            <div className="space-y-3">
              <div className="space-y-1 grid">
                <Label>
                  Amendment Type <span className="text-destructive">*</span>
                </Label>
                <Combobox
                  disabled={!isEditable}
                  dwimOnChange
                  isModal={false}
                  placeholder="Select amendment type..."
                  options={AMENDMENT_TYPE_SELECT_OPTIONS}
                  value={amendmentType}
                  onValueChange={onChangeAmendmentType}
                  virtualizeList
                  popoverClassName="w-[420px] h-[280px]"
                />
                {errorAmendmentType && (
                  <p className="text-destructive text-xs">{errorAmendmentType}</p>
                )}
              </div>

              <div className="grid gap-1">
                <Label htmlFor="ttv2-contract-number">Contract Number</Label>
                <Input
                  disabled={!isEditable}
                  id="ttv2-contract-number"
                  value={contractNumber}
                  onChange={(e) => onChangeContractNumber(e.target.value)}
                  placeholder="Enter contract number..."
                />
              </div>
            </div>
          )}

          {otcContractMode !== null && otcContractMode !== "amendment" && (
            <div className="space-y-1 grid">
              <Label>
                OTC Contract Type <span className="text-destructive">*</span>
              </Label>
              <Combobox
                disabled={!isEditable}
                dwimOnChange
                isModal={false}
                placeholder="Select contract type..."
                options={availableOtcContractTypes}
                value={otcContractType}
                onValueChange={onChangeOtcContractType}
                virtualizeList
                popoverClassName="w-[420px] h-[280px]"
              />
              {errorOtcContractType && (
                <p className="text-destructive text-xs">{errorOtcContractType}</p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
